/**
 * Global, delegated behavior for every `input[type="number"]` in the app.
 *
 * This intentionally is NOT an Angular directive. A selector-matched
 * directive only applies within the NgModule(s) that import it, so new
 * feature modules can silently miss it (as happened here previously).
 * Delegating from `document` instead guarantees the behavior applies to
 * every number input, in every module, with no per-module wiring required.
 *
 * Call `initNumericOnlyGlobalHandlers()` exactly once (from the app root).
 *
 * Behavior:
 * - Restricts typed input to digits (and a single decimal point when the
 *   input's `step` allows fractional values).
 * - Sanitizes pasted content the same way.
 * - Blocks the browser's native "scroll over a focused number input changes
 *   its value" behavior. A focused number input also captures the wheel
 *   event itself, which would otherwise stop the page from scrolling under
 *   the cursor - so we preventDefault the native behavior and manually
 *   forward the scroll delta to the nearest scrollable ancestor, keeping
 *   scrolling smooth and continuous instead of the value "wiggling".
 */

const ALLOWED_KEYS = [
   'Backspace',
   'Delete',
   'Tab',
   'Escape',
   'Enter',
   'ArrowLeft',
   'ArrowRight',
   'ArrowUp',
   'ArrowDown',
   'Home',
   'End',
];

function isNumberInput(target: EventTarget | null): target is HTMLInputElement {
   return target instanceof HTMLInputElement && target.type === 'number';
}

function allowsDecimal(input: HTMLInputElement): boolean {
   const step = input.step;
   return !!step && !isNaN(parseFloat(step)) && parseFloat(step) < 1;
}

function sanitize(input: HTMLInputElement): void {
   const allowDecimal = allowsDecimal(input);
   let cleaned: string;

   if (allowDecimal) {
      // Remove any invalid characters, but keep digits and a single decimal point
      cleaned = input.value.replace(/[^\d.]/g, '');

      // Ensure only one decimal point
      const parts = cleaned.split('.');
      if (parts.length > 2) {
         cleaned = parts[0] + '.' + parts.slice(1).join('');
      }

      // Handle cases where decimal is at start or end
      if (cleaned === '.') {
         cleaned = '0.';
      } else if (cleaned.startsWith('.') && cleaned.length > 1) {
         cleaned = '0' + cleaned;
      }
   } else {
      // Remove everything non-digit
      cleaned = input.value.replace(/\D/g, '');
   }

   if (cleaned !== input.value) {
      input.value = cleaned;
   }
}

function onKeyDown(event: KeyboardEvent): void {
   const target = event.target;
   if (!isNumberInput(target)) {
      return;
   }

   if (ALLOWED_KEYS.includes(event.key)) {
      return;
   }

   // Allow Ctrl/Cmd combos (copy/paste/select all)
   if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
      return;
   }

   // Allow decimal point if decimals are allowed
   if (allowsDecimal(target) && event.key === '.') {
      // Prevent multiple decimal points
      if (target.value.includes('.')) {
         event.preventDefault();
      }
      return;
   }

   // Block anything that isn't a digit
   if (!/^\d$/.test(event.key)) {
      event.preventDefault();
   }
}

function onPaste(event: ClipboardEvent): void {
   const target = event.target;
   if (!isNumberInput(target)) {
      return;
   }
   event.preventDefault();

   const pasteData = event.clipboardData?.getData('text') ?? '';
   const sanitized = allowsDecimal(target) ? pasteData.replace(/[^\d.]/g, '') : pasteData.replace(/\D/g, '');

   const start = target.selectionStart ?? target.value.length;
   const end = target.selectionEnd ?? target.value.length;

   target.value = target.value.substring(0, start) + sanitized + target.value.substring(end);

   target.dispatchEvent(new Event('input', { bubbles: true }));
}

function onInput(event: Event): void {
   const target = event.target;
   if (!isNumberInput(target)) {
      return;
   }
   sanitize(target);
}

function isScrollable(element: HTMLElement): boolean {
   const style = getComputedStyle(element);
   const overflowY = style.overflowY;
   const canScrollY = (overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight;
   const overflowX = style.overflowX;
   const canScrollX = (overflowX === 'auto' || overflowX === 'scroll') && element.scrollWidth > element.clientWidth;
   return canScrollY || canScrollX;
}

// Finds the nearest ancestor that actually scrolls, so we can hand the
// wheel delta off to it instead of always scrolling the whole window (e.g.
// when the number input sits inside a scrollable dialog or table).
function getScrollableAncestor(element: HTMLElement): HTMLElement | null {
   let parent = element.parentElement;
   while (parent) {
      if (isScrollable(parent)) {
         return parent;
      }
      parent = parent.parentElement;
   }
   return null;
}

function onWheel(event: WheelEvent): void {
   const target = event.target;
   if (!isNumberInput(target)) {
      return;
   }

   // Stop the browser's native "scroll changes the value" behavior. A
   // focused number input also swallows the wheel event by default, which
   // is why scrolling over it otherwise does nothing to the page.
   event.preventDefault();

   const scrollTarget = getScrollableAncestor(target);
   if (scrollTarget) {
      scrollTarget.scrollBy({ left: event.deltaX, top: event.deltaY });
   } else {
      window.scrollBy({ left: event.deltaX, top: event.deltaY });
   }
}

let initialized = false;

export function initNumericOnlyGlobalHandlers(): void {
   if (initialized) {
      return;
   }
   initialized = true;

   document.addEventListener('keydown', onKeyDown);
   document.addEventListener('paste', onPaste);
   document.addEventListener('input', onInput);
   // Must be non-passive: we call preventDefault to block the native
   // value-changing behavior before forwarding the scroll ourselves.
   document.addEventListener('wheel', onWheel, { passive: false });
}
